/**
 * @license
 * Copyright Tyler Technologies, Inc. 
 * License: Apache-2.0
 */
var _a;
import { __decorate } from "tslib";
import { CUSTOM_ELEMENT_NAME_PROPERTY, debounce } from '@tylertech/forge-core';
import { html, unsafeCSS } from 'lit';
import { customElement, property, queryAssignedElements, queryAssignedNodes, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { setDefaultAria } from '../../core/utils/a11y-utils';
import { toggleState } from '../../core/utils/utils';
import { BaseLitElement } from '../../core/base/base-lit-element';
const styles = ':host{display:inline}.forge-meter-group{--_meter-group-background:var(--forge-meter-group-background, var(--forge-theme-surface-container-low, #ebebeb));--_meter-group-height:var(--forge-meter-group-height, var(--forge-spacing-medium, 16px));--_meter-group-shape:var(--forge-meter-group-shape, calc(var(--forge-shape-medium, 4px) * var(--forge-shape-factor, 1)));--_meter-group-tickmarks:var(--forge-meter-group-tickmarks, 10);--_meter-group-tickmark-color:var(--forge-meter-group-tickmark-color, var(--forge-theme-outline-high, #212121));--_meter-group-tickmark-opacity:var(--forge-meter-group-tickmark-opacity, 54%)}.forge-meter-group{box-sizing:border-box}.forge-meter-group .heading{-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;font-family:var(--forge-typography-label1-font-family, var(--forge-typography-font-family, \"Roboto\", sans-serif));font-size:var(--forge-typography-label1-font-size, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-label-font-size-scale, .75)));font-weight:var(--forge-typography-label1-font-weight,400);line-height:var(--forge-typography-label1-line-height, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-label-line-height-scale, 1.25)));letter-spacing:var(--forge-typography-label1-letter-spacing, .0357142857em);text-transform:var(--forge-typography-label1-text-transform,inherit);text-decoration:var(--forge-typography-label1-text-decoration,inherit);display:flex;align-items:center;justify-content:space-between;line-height:normal}.forge-meter-group .heading.not-empty{margin-bottom:var(--forge-spacing-xxsmall,4px)}.forge-meter-group .label{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.forge-meter-group .value{color:var(--forge-theme-text-medium,rgba(0,0,0,.6))}.forge-meter-group .track{position:relative;height:var(--_meter-group-height);border-radius:var(--_meter-group-shape);overflow:hidden;background:var(--_meter-group-background)}.forge-meter-group .track.tickmarks::after{content:\"\";position:relative;display:block;z-index:9999;width:100%;height:100%;margin-left:-1px;background:repeating-linear-gradient(90deg,var(--_meter-group-tickmark-color) 0,var(--_meter-group-tickmark-color) 1px,transparent 1px,transparent calc(100% / (var(--_meter-group-tickmarks) + 1) + 0px));opacity:var(--_meter-group-tickmark-opacity);pointer-events:none}.forge-meter-group .track.inner-shape--inherit{--forge-meter-inner-shape:var(--_meter-group-shape)}.forge-meter-group .track.shape--default{--_meter-group-shape:var(--forge-meter-group-shape, calc(var(--forge-shape-medium, 4px) * var(--forge-shape-factor, 1)))}.forge-meter-group .track.shape--rounded{--_meter-group-shape:var(--forge-meter-group-shape, calc(var(--forge-shape-full, 9999px) * var(--forge-shape-factor, 1)))}.forge-meter-group .track.shape--squared{--_meter-group-shape:var(--forge-meter-group-shape, 0)}.forge-meter-group .track.density--small{--_meter-group-height:var(--forge-meter-group-height, var(--forge-spacing-xsmall, 8px))}.forge-meter-group .track.density--medium{--_meter-group-height:var(--forge-meter-group-height, var(--forge-spacing-medium, 16px))}.forge-meter-group .track.density--large{--_meter-group-height:var(--forge-meter-group-height, var(--forge-spacing-large, 24px))}:host(:is(:state(vertical),:--vertical)){display:block}:host(:is(:state(vertical),:--vertical)) .forge-meter-group{display:flex;flex-direction:row-reverse;align-items:end;width:fit-content;height:100%;max-width:100%}:host(:is(:state(vertical),:--vertical)) .forge-meter-group .heading{flex-direction:column;align-items:start}:host(:is(:state(vertical),:--vertical)) .forge-meter-group .heading.not-empty{margin-bottom:0;margin-left:var(--forge-spacing-xsmall,8px)}:host(:is(:state(vertical),:--vertical)) .forge-meter-group .track{width:var(--_meter-group-height);height:100%}:host(:is(:state(vertical),:--vertical)) .forge-meter-group .track.tickmarks::after{margin-top:1px;margin-left:0;background:repeating-linear-gradient(90deg,var(--_meter-group-tickmark-color),true 0,var(--_meter-group-tickmark-color),true 1px,transparent 1px,transparent calc(100% / (var(--_meter-group-tickmarks) + 1) + 0px))}@media (forced-colors:active){.track{border:1px solid CanvasText}}';
export const METER_GROUP_TAG_NAME = 'forge-meter-group';
/**
 * @tag forge-meter-group
 *
 * @summary Meter groups display several meters together on one track.
 *
 * @state vertical - Applied when the meter group is oriented vertically.
 *
 * @cssproperty --forge-meter-group-background - The background color of the meter group.
 * @cssproperty --forge-meter-group-height - The block size of the meter group.
 * @cssproperty --forge-meter-group-shape - The border radius of the meter group.
 * @cssproperty --forge-meter-group-tickmarks - The number of tickmarks to display.
 * @cssproperty --forge-meter-group-tickmark-color - The color of the tickmarks.
 * @cssproperty --forge-meter-group-tickmark-opacity - The opacity of the tickmarks.
 *
 * @csspart root - The root container element.
 * @csspart track - The element comprising the meter group's background.
 *
 * @slot - The default slot for grouped `<forge-meter>` elements.
 * @slot label - Positions a label above the meter group.
 * @slot value - A textual representation of the meter's value.
 */
let MeterGroupComponent = class MeterGroupComponent extends BaseLitElement {
    get labels() {
        return this._internals.labels;
    }
    get form() {
        return this._internals.form;
    }
    constructor() {
        super();
        /**
         * The minimum value of each meter in the group.
         * @default 0
         * @attribute
         */
        this.min = 0;
        /**
         * The maximum value of each meter in the group.
         * @default 1
         * @attribute
         */
        this.max = 1;
        /**
         * Whether to display tickmarks.
         * @default false
         * @attribute
         */
        this.tickmarks = false;
        /**
         * Whether the meter is oriented horizontally or vertically.
         * @default 'horizontal'
         * @attribute
         */
        this.direction = 'horizontal';
        /**
         * The density of the meter group.
         * @default 'default'
         * @attribute
         */
        this.density = 'default';
        /**
         * The shape of the meter group.
         * @default 'default'
         * @attribute
         */
        this.shape = 'default';
        /**
         * The shape of each meter in the group.
         * @default 'default'
         * @attribute inner-shape
         */
        this.innerShape = 'default';
        this._hasSlottedHeadingContent = false;
        /**
         * Debounce the handler to reduce the number of times it's called during rapid changes,
         * especially when the component is first connected.
         */
        this._debounceMeterChange = debounce(this._handleMeterChange.bind(this), 0);
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        setDefaultAria(this, this._internals, { role: 'group' });
    }
    willUpdate(changedProperties) {
        const keys = Array.from(changedProperties.keys());
        if (keys.includes('direction')) {
            toggleState(this._internals, 'vertical', this.direction === 'vertical');
        }
        if (keys.some(key => ['direction', 'min', 'max'].includes(key.toString()))) {
            this._syncMeters();
        }
    }
    render() {
        return html `
      <div part="root" class="forge-meter-group" @change=${this._debounceMeterChange}>
        <div class=${classMap({ heading: true, 'not-empty': this._hasSlottedHeadingContent })} @slotchange=${this._handleHeadingSlotChange}>
          <div class="label"><slot name="label"></slot></div>
          <div class="value"><slot name="value"></slot></div>
        </div>
        <div
          part="track"
          class=${classMap({
            track: true,
            tickmarks: this.tickmarks,
            [`density--${this.density}`]: true,
            [`inner-shape--${this.innerShape}`]: true,
            [`shape--${this.shape}`]: true
        })}>
          <slot @slotchange=${this._debounceMeterChange}></slot>
        </div>
      </div>
    `;
    }
    /**
     * Applies the min and max values to each meter in the group. These should always be set on the
     * meter group component to ensure that all meters are in sync.
     */
    _syncMeters() {
        this._meters.forEach(meter => {
            meter.direction = this.direction;
            meter.min = this.min;
            meter.max = this.max;
        });
    }
    /**
     * Handles changes to the meters in the group, updating their arrangement relative to each other.
     */
    _handleMeterChange(evt) {
        evt.stopPropagation();
        // Iterate through each slotted meter, applying z-indices based on their order and setting an
        // inset equal to the widths of all preceding meters.
        this._meters.reduce((inset, meter, index, meters) => {
            meter.style.setProperty('--_meter-z-index', `${meters.length - index}`);
            meter.style.setProperty('--_meter-inset', `${inset}%`);
            return inset + meter.percentage;
        }, 0);
    }
    /**
     * Checks whether the meter group has any slotted heading content.
     */
    _handleHeadingSlotChange() {
        const nodes = [...this._labelNodes, ...this._valueNodes].filter(node => !!node.textContent?.trim());
        this._hasSlottedHeadingContent = !!nodes.length;
    }
};
_a = CUSTOM_ELEMENT_NAME_PROPERTY;
/* @ignore */
MeterGroupComponent.styles = unsafeCSS(styles);
MeterGroupComponent.formAssociated = true;
/** @deprecated Used for compatibility with legacy Forge @customElement decorator. */
MeterGroupComponent[_a] = METER_GROUP_TAG_NAME;
__decorate([
    property({ type: Number })
], MeterGroupComponent.prototype, "min", void 0);
__decorate([
    property({ type: Number })
], MeterGroupComponent.prototype, "max", void 0);
__decorate([
    property({ type: Boolean })
], MeterGroupComponent.prototype, "tickmarks", void 0);
__decorate([
    property()
], MeterGroupComponent.prototype, "direction", void 0);
__decorate([
    property()
], MeterGroupComponent.prototype, "density", void 0);
__decorate([
    property()
], MeterGroupComponent.prototype, "shape", void 0);
__decorate([
    property({ attribute: 'inner-shape' })
], MeterGroupComponent.prototype, "innerShape", void 0);
__decorate([
    state()
], MeterGroupComponent.prototype, "_hasSlottedHeadingContent", void 0);
__decorate([
    queryAssignedNodes({ slot: 'label' })
], MeterGroupComponent.prototype, "_labelNodes", void 0);
__decorate([
    queryAssignedNodes({ slot: 'value' })
], MeterGroupComponent.prototype, "_valueNodes", void 0);
__decorate([
    queryAssignedElements({ selector: 'forge-meter' })
], MeterGroupComponent.prototype, "_meters", void 0);
MeterGroupComponent = __decorate([
    customElement(METER_GROUP_TAG_NAME)
], MeterGroupComponent);
export { MeterGroupComponent };
